// IMPORTS
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useState } from 'react'

// COMPONENTS
import Card from '@mui/material/Card'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import DragIcon from './DragIcon'

// UTILS
import { formatGrade, hasRole } from '../../../utils/utilityFuncs'

const defaultImg = 'https://lesson-images-legacy.s3-us-west-1.amazonaws.com/img/img-thumb.png'

const linkSx = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'block'
}

const cardShell = {
  width: 300,
  m: '10px',
  p: 0,
  overflow: 'hidden',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 8px 24px rgba(21,101,192,0.08)'
  },
  '&:hover .lesson-card-cover': {
    transform: 'scale(1.03)'
  }
}

const CardView = ({
  grade,
  imageThumbnail,
  subject,
  author,
  title,
  order,
  description,
  slug,
  classSlug,
  notDraggable,
  isFree,
  isArchived
}) => {
  const { firstName, lastName } = author
  const { roles } = useSelector(state => state.auth.user)
  const canEdit = hasRole(['author', 'superadmin'], roles) && !notDraggable
  const isStudent = hasRole(['student'], roles)

  const returnURL = () => {
    const lessonsPath = `/lessons/${slug}`
    return isStudent ? `/classes/${classSlug}${lessonsPath}` : lessonsPath
  }

  const onToggleClicked = (e) => {
    e.cancelBubble = true
    e.stopPropagation()
    toggleTruncate(!truncateLines)
  }

  const [truncateLines, toggleTruncate] = useState(true)

  return (
    <Card elevation={0} sx={cardShell}>
      <Box component={Link} to={returnURL()} sx={linkSx}>
        <Stack
          direction='row'
          sx={{
            width: '100%',
            alignItems: 'stretch',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box
            sx={{
              flex: '1 1 50%',
              minWidth: 0,
              px: 1.25,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.08)'
            }}
          >
            <Typography
              variant='caption'
              noWrap
              component='div'
              sx={{
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                lineHeight: 1.3
              }}
            >
              {subject}
            </Typography>
          </Box>
          <Stack direction='row' sx={{ flex: '1 1 50%', minWidth: 0 }}>
            <Box
              sx={{
                flex: !canEdit ? '1 1 100%' : '3 1 0',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
                py: 0.75,
                bgcolor: 'grey.900',
                color: 'grey.100'
              }}
            >
              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                {formatGrade(grade)}
              </Typography>
            </Box>
            {(canEdit && !notDraggable) && (
              <DragIcon sx={{ flex: '1 1 0', minWidth: 0 }} />
            )}
          </Stack>
        </Stack>
        {notDraggable && (
          <Box
            className='lesson-card-cover'
            sx={{
              position: 'relative',
              bgcolor: 'grey.200',
              aspectRatio: '16 / 10',
              maxHeight: 200,
              overflow: 'hidden',
              transition: 'transform 0.35s ease'
            }}
          >
            <Box
              component='img'
              src={imageThumbnail || defaultImg}
              alt=''
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
            <Box
              sx={{
                pointerEvents: 'none',
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.35), transparent 45%)'
              }}
            />
          </Box>
        )}
        <Box sx={{ px: 1.5, py: 1.25, bgcolor: 'grey.50' }}>
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 500, display: 'block', mb: 0.25 }}>
            Lesson
          </Typography>
          <Typography variant='subtitle2' component='div' fontWeight={700} sx={{ lineHeight: 1.35 }}>
            {title}
          </Typography>
        </Box>
      </Box>
      {!notDraggable && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            bgcolor: 'grey.100',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 500, mr: 0.75 }}>
            Order
          </Typography>
          <Typography variant='body2' component='span' fontWeight={700}>
            {order}
          </Typography>
        </Box>
      )}
      {notDraggable && (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            bgcolor: 'grey.50',
            minHeight: 88,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ lineHeight: 1.5 }}
            noWrap={truncateLines}
          >
            {description}
          </Typography>
          {(description || '').length > 60 && (
            <Button
              variant='text'
              size='small'
              color='primary'
              sx={{ mt: 0.75, px: 0.5, minWidth: 0, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
              onClick={onToggleClicked}
            >
              {truncateLines ? 'More' : 'Less'}
            </Button>
          )}
        </Box>
      )}
      <Box
        component={Link}
        to={returnURL()}
        sx={{
          ...linkSx,
          px: 1.5,
          py: 1.25,
          bgcolor: 'grey.100',
          borderTop: '1px solid',
          borderColor: 'divider',
          transition: 'background-color 0.15s',
          '&:hover': { bgcolor: 'grey.200' }
        }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1} flexWrap='wrap' useFlexGap>
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 500, display: 'block' }}>
              Author
            </Typography>
            <Typography variant='body2' fontWeight={600} noWrap>
              {`${firstName} ${lastName}`}
            </Typography>
          </Box>
          <Stack direction='row' spacing={0.75} sx={{ flexShrink: 0 }}>
            {isArchived && (
              <Chip label='Archived' size='small' color='error' variant='outlined' sx={{ fontWeight: 600 }} />
            )}
            {isFree && (
              <Chip label='Free' size='small' color='primary' sx={{ fontWeight: 600 }} />
            )}
          </Stack>
        </Stack>
      </Box>
    </Card>
  )
}

export default CardView
